import { useGetChapterStudentProgress } from "hooks/api/student-progress";
import { useRole } from "hooks/common";
import { useIsClassroom } from "hooks/common/useIsClassroom/useIsClassroom";
import { useEffect, useMemo, useRef } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import cx from "classnames";
import {
  useAppDispatch,
  useAppSelector,
  useExerciseConstructor,
  useLessonSettings,
} from "hooks/redux";
import { useScrollAndHighlight } from "hooks/common/useScrollAndHighlight";
import { useExercises } from "hooks/api/exercises";
import { LessonDetailsContent } from "content";
import { Button } from "components/Interactive/Button";
import { Divider } from "components/Common/Divider";
import { StudentSwitcher } from "components/Common/StudentSwitcher";
import { ChapterNameText, ExercisesPreview } from "components/Lesson";
import type { TCourseLessonChapter } from "types/api/course";
import "./LessonContentChapter.styles.scss";
import { exerciseSelector, setExerciseState } from "store/features/exercises";
import { da } from "date-fns/locale";
type LessonContentChapterProps = {
  /**
   * Lesson chapter data to display.
   */
  chapter: TCourseLessonChapter;
  /**
   * The chapter sequence order.
   * @default 1
   */
  order?: number;
  /**
   * Indicates active state of chapter content item.
   * @default false
   */
  active?: boolean;
  /**
   * Student ID for group context (when studentId is not in URL params)
   * @default undefined
   */
  overrideStudentId?: string;
  /**
   * Props for StudentSwitcher component (for groups)
   * @default undefined
   */
  studentSwitcherProps?: {
    studentIds: number[];
    selectedStudentId?: number;
    onStudentSelect: (studentId: number) => void;
    loading?: boolean;
  };
  /**
   * Definition ID of the exercise to deep-link to within this chapter.
   * @default null
   */
  deepLinkExerciseId?: number | null;
  /**
   * Seconds into the video the deep-linked VIDEO exercise should start at.
   * @default null
   */
  deepLinkStartSeconds?: number | null;
};

export const LessonContentChapter: React.FC<LessonContentChapterProps> = (
  props
) => {
  const {
    chapter,
    order = 1,
    active = false,
    overrideStudentId,
    studentSwitcherProps,
    deepLinkExerciseId = null,
    deepLinkStartSeconds = null,
  } = props;

  const { courseId, lessonId, studentId: urlStudentId } = useParams();
  const studentId = overrideStudentId || urlStudentId;
  const { isUserAdmin } = useRole();

  const { isClassroom } = useIsClassroom();

  const ref = useRef<HTMLDivElement | null>(null);
  const { highlight } = useScrollAndHighlight(ref.current, {
    enable: active,
    highlightTime: 2000,
    scrollOffset: 20,
    delay: 50, // We need to delay for a bit before ref hook assign div element
  });

  const { openExerciseList, setChapterId } = useExerciseConstructor();
  const { viewMode, isExerciseLiveMode } = useLessonSettings();

  const { data: exercisesData, isLoading: exercisesLoading } = useExercises(
    !isClassroom ? courseId : null,
    !isClassroom ? lessonId : null,
    !isClassroom ? chapter.id : null
  );

  const { data: progressData, isLoading: progressLoading } =
    useGetChapterStudentProgress(
      isClassroom ? courseId : null,
      isClassroom ? lessonId : null,
      isClassroom ? chapter.id : null,
      isClassroom ? studentId : null
    );

  const chapterExercises = useMemo(
    () =>
      isClassroom ? progressData?.filter((e) => !e.isHomework) : exercisesData,
    [isClassroom, progressData, exercisesData]
  );
  const isLoading = isClassroom ? progressLoading : exercisesLoading;

  const openExerciseConstructorClickHandler = () => {
    setChapterId(chapter.id);
    openExerciseList();
  };

  const dispatch = useAppDispatch();

  const { exercises: localExercises } = useAppSelector(exerciseSelector);

  useEffect(() => {
    if (!chapterExercises) return;
    dispatch(setExerciseState(chapterExercises));
  }, [chapterExercises]);

  return (
    <div ref={ref} className="nb-lesson-details-content-chapter-wrapper">
      <div className="nb-lesson-details-content-chapter">
        <div
          className={cx([
            "nb-lesson-details-content-chapter-title",
            {
              ["nb-lesson-details-content-chapter-title--highlight"]: highlight,
            },
          ])}
        >
          <div className="nb-lesson-details-content-chapter-title-container">
            <div className="nb-lesson-details-content-chapter-title-text">
              <ChapterNameText text={chapter.name} />
            </div>
            {/* Student Switcher for Groups */}
            {studentSwitcherProps &&
              studentSwitcherProps.studentIds.length > 1 && (
                <div className="nb-lesson-details-content-chapter-title-switcher">
                  <StudentSwitcher
                    studentIds={studentSwitcherProps.studentIds}
                    selectedStudentId={studentSwitcherProps.selectedStudentId}
                    onStudentSelect={studentSwitcherProps.onStudentSelect}
                    loading={studentSwitcherProps.loading}
                  />
                </div>
              )}
          </div>
        </div>
        <ExercisesPreview
          exercises={localExercises}
          chapterOrder={order}
          chapterId={chapter.id}
          loading={isLoading}
          isHomeworkExercises={false}
          deepLinkExerciseId={deepLinkExerciseId}
          deepLinkStartSeconds={deepLinkStartSeconds}
        />
        {viewMode === "constructor" && !isExerciseLiveMode && isUserAdmin && (
          <Button
            variant="secondary"
            fullWidth
            onClick={openExerciseConstructorClickHandler}
          >
            {LessonDetailsContent.Content.Lesson.Button.ADD}
          </Button>
        )}
      </div>
      <Divider
        margin="yAxis"
        marginSize="large"
        className="nb-lesson-details-content-chapter-divider"
      />
    </div>
  );
};
